import SitesService from '@/services/SitesService';

const defaultState = () => ({
  sites: [],
  caps: { enabled: false, maxSites: 0, customDomain: false, maxCustomDomains: 0, used: 0 },
  // false until castr's caps arrived: the defaults above are placeholders, not a plan
  capsLoaded: false,
  sitesLoaded: false,
  teamPermissionError: false,
});

const state = defaultState();

const mutations = {
  SET_SITES(state, { sites, caps }) {
    state.sites = sites || [];
    if (caps) {
      state.caps = caps;
      state.capsLoaded = true;
    }
    state.sitesLoaded = true;
  },
  SET_CAPS(state, caps) {
    if (!caps) return;
    state.caps = caps;
    state.capsLoaded = true;
  },
  UPSERT_SITE(state, site) {
    const index = state.sites.findIndex((s) => s.id === site.id);
    if (index >= 0) {
      state.sites.splice(index, 1, site);
    } else {
      state.sites.unshift(site);
      // a deep-linked site joins the list before /sites ran: it is not a new one
      if (state.sitesLoaded) state.caps = { ...state.caps, used: (state.caps.used || 0) + 1 };
    }
  },
  REMOVE_SITE(state, id) {
    const index = state.sites.findIndex((s) => s.id === id);
    if (index >= 0) {
      state.sites.splice(index, 1);
      state.caps = { ...state.caps, used: Math.max(0, (state.caps.used || 0) - 1) };
    }
  },
  SET_TEAM_PERMISSION_ERROR(state, error) {
    state.teamPermissionError = error;
  },
  UNLOAD_SITES(state) {
    Object.assign(state, defaultState());
  },
};

const actions = {
  async getSites({ commit }) {
    const result = await SitesService.getSites();
    commit('SET_SITES', result);
    return result;
  },
  async loadCaps({ commit }) {
    const caps = await SitesService.getSitesCaps();
    commit('SET_CAPS', caps);
    return caps;
  },
  async getSite({ commit }, id) {
    const site = await SitesService.getSite(id);
    commit('UPSERT_SITE', site);
    return site;
  },
  async createSite({ commit }, payload) {
    const site = await SitesService.createSite(payload);
    commit('UPSERT_SITE', site);
    return site;
  },
  async updateSite({ commit }, { id, payload }) {
    const site = await SitesService.updateSite(id, payload);
    commit('UPSERT_SITE', site);
    return site;
  },
  /** Resolves with the publish envelope `{ site, publishedKeys, publish, runtime }`. */
  async publishSite({ commit }, id) {
    const result = await SitesService.publishSite(id);
    commit('UPSERT_SITE', result.site);
    return result;
  },
  async deleteSite({ commit }, id) {
    const result = await SitesService.deleteSite(id);
    commit('REMOVE_SITE', id);
    return result;
  },
  setTeamPermissionError({ commit }, { error }) {
    commit('SET_TEAM_PERMISSION_ERROR', error);
  },
  unloadSites({ commit }) {
    commit('UNLOAD_SITES');
  },
};

const getters = {
  sites: (state) => state.sites,
  caps: (state) => state.caps,
  capsLoaded: (state) => state.capsLoaded,
  customDomainLocked: (state) => state.capsLoaded && state.caps?.customDomain === false,
  sitesLoaded: (state) => state.sitesLoaded,
  siteById: (state) => (id) => state.sites.find((s) => s.id === id) || null,
  teamPermissionError: (state) => state.teamPermissionError,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
